import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Tooltip from '@mui/material/Tooltip';
import swal from 'sweetalert';

import DropDown from './components/DropDown';
import Client from './components/Client';
import Config from './components/ClientConfig';
import FeedbackFilters from './components/FeedbackFilters';
import Avatar from '@mui/material/Avatar';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";

const DEFAULT_LIMIT = 100;
const EXPORT_LIMIT = 5000;

const FEEDBACK_TYPES = [
  { iref: 0, name: 'OK' },
  { iref: 1, name: 'Info' },
  { iref: 2, name: 'Warning' },
  { iref: 3, name: 'Error' },
  { iref: 4, name: 'Debug' },
];
const TYPE_OPTIONS = Object.fromEntries(FEEDBACK_TYPES.map(t => [String(t.iref), t.name]));
const typeLabel = (t) => ({0:'OK',1:'Info',2:'Warning',3:'Error',4:'Debug'})[t] ?? '';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: "#273442",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}));

function toCSV(rows) {
  if (!rows || !rows.length) return "";

  const headers = ["iref","type","typeLabel","module","code","message","languageIref","languageName"];

  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g,'""')}"`;
    return s;
  };

  const lines = [headers.join(",")];

  rows.forEach(r => {
    lines.push([
      r.iref ?? "",
      r.type ?? "",
      typeLabel(r.type),
      r.module ?? "",
      r.code ?? "",
      r.message ?? "",
      r.language?.iref ?? "",
      r.language?.languageName ?? "" // ✅ keep diacritics
    ].map(esc).join(","));
  });

  return lines.join("\n");
}

function downloadCSV(filename, csvString) {
  // ✅ BOM -> Excel reads UTF-8 diacritics correctly
  const blob = new Blob(["\uFEFF" + csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

class FeedbackRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client("none"),
      edit: !!props.edit,
      row: props.row || {},
      valid: { module: false, code: false, message: false, type: false, language: false },
      changed: false,
      errorMessage: null,
    };
  }

  componentDidMount() {
    this.recalcValid(this.state.row);
  }

  recalcValid = (row) => {
    const v = { ...this.state.valid };
    v.module = !!(row.module && row.module.length > 0);
    v.code = !!(row.code && row.code.length > 0);
    v.message = !!(row.message && row.message.length > 0);
    v.type = (row.type || row.type === 0);
    v.language = !!row.language;
    this.setState({ valid: v });
  }

  handleEditClick = () => this.setState({ edit: !this.state.edit });

  changeType = (selectedIref) => {
    const row = { ...this.state.row };
    row.type = Number(selectedIref);
    this.setState({ row, changed: true }, () => this.recalcValid(row));
  }

  changeLanguage = (langIref) => {
    const row = { ...this.state.row };
    const langs = this.props.languages || {};
    row.language = langs[langIref] || {};
    this.setState({ row, changed: true }, () => this.recalcValid(row));
  }

  // ✅ module from list (exact)
  changeModule = (moduleName) => {
    const row = { ...this.state.row };
    row.module = moduleName || '';
    this.setState({ row, changed: true }, () => this.recalcValid(row));
  }

  changeValue = (e) => {
    const id = e?.target?.id || null;
    let value = e?.target?.value ?? null;
    if (!id) return;

    const row = { ...this.state.row };

    if (id === 'code')    row.code = (value || '').toUpperCase().slice(0,16);
    if (id === 'message') row.message = value || '';

    this.setState({ row, changed: true }, () => this.recalcValid(row));
  }

  pushData = () => {
    const { valid, changed, row } = this.state;
    if (!(changed && valid.module && valid.code && valid.message && valid.type && valid.language)) {
      swal("Error", "Add/Update feedback failed! Some fields are not validated!", "error");
      return;
    }

    const rdata = this.state.client.pushData(Config.API_URL_CONFIG_FEEDBACK_ADD, row);
    if (!rdata) return;

    if (rdata.status === "OK") {
      const iref = rdata.data?.iref || row.iref;
      this.setState({ row: { ...row, iref } });
      this.handleEditClick();
      swal("Success", "Successfully added/modified feedback!", "success", { buttons: false, timer: 1000 })
        .then(()=>{ this.setState({ edit:false, changed:false }); this.props.rowAdded && this.props.rowAdded(); });
    } else {
      this.setState({ errorMessage: rdata.message || null });
      swal("Error", (rdata.message || "Add/Update feedback failed!"), "error");
    }
  }

  render(){
    const { languages, modules = [] } = this.props;
    const { row, edit, valid, changed } = this.state;

    const iref = row.iref || "0";
    const typeVal = (row.type || row.type === 0) ? row.type : '';
    const moduleVal = row.module || '';
    const codeVal = row.code || '';
    const messageVal = row.message || '';
    const languageIref = row.language?.iref || '';
    const languageName = row.language?.languageName || '';
    const showSave = edit && changed && valid.module && valid.code && valid.message && valid.type && valid.language;

    // Build dropdown options for modules
    const MODULE_OPTIONS = {};
    (modules || []).forEach(m => { if (m) MODULE_OPTIONS[m] = m; });

    return (
      <StyledTableRow key={iref}>
        <StyledTableCell align="left" sx={{ whiteSpace:'nowrap' }}>{iref}</StyledTableCell>

        <StyledTableCell align="left" sx={{ minWidth:140 }}>
          {edit ? (
            <DropDown
              name="Type"
              value={String(typeVal)}
              doptions={TYPE_OPTIONS}
              hasIcons={false}
              minWidth={120}
              onChangeFunction={this.changeType}
            />
          ) : (FEEDBACK_TYPES.find(t => t.iref === typeVal)?.name || '')}
        </StyledTableCell>

        {/* ✅ Module dropdown instead of "contains" textfield */}
        <StyledTableCell align="left" sx={{ minWidth:220 }}>
          {edit ? (
            <DropDown
              name="Module"
              value={moduleVal}
              doptions={MODULE_OPTIONS}
              hasIcons={false}
              minWidth={200}
              onChangeFunction={this.changeModule}
            />
          ) : moduleVal}
        </StyledTableCell>

        <StyledTableCell align="left" sx={{ minWidth:160, fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
          {edit ? (
            <TextField
              id="code"
              label="Code"
              variant="outlined"
              value={codeVal}
              onChange={this.changeValue}
              onFocus={e=>e.target.select()}
              inputProps={{ maxLength:"16" }}
              error={!valid.code}
              required
              size="small"
            />
          ) : codeVal}
        </StyledTableCell>

        <StyledTableCell align="left" sx={{ maxWidth: 520 }}>
          {edit ? (
            <TextField
              id="message"
              label="Message"
              variant="outlined"
              value={messageVal}
              onChange={this.changeValue}
              onFocus={e=>e.target.select()}
              multiline
              minRows={1}
              maxRows={3}
              fullWidth
              size="small"
              error={!valid.message}
              required
            />
          ) : (
            <span style={{
              display:'inline-block',
              maxWidth: 520,
              overflow:'hidden',
              textOverflow:'ellipsis',
              whiteSpace:'nowrap',
              verticalAlign:'bottom'
            }}>
              {messageVal}
            </span>
          )}
        </StyledTableCell>

        <StyledTableCell align="left" sx={{ minWidth:160 }}>
          {edit ? (
            <DropDown
              name="Language"
              value={languageIref}
              doptions={languages}
              hasIcons={true}
              minWidth={140}
              onChangeFunction={this.changeLanguage}
            />
          ) : languageName}
        </StyledTableCell>

        <StyledTableCell align="right" sx={{ whiteSpace:'nowrap', position:'sticky', right:0, background:'inherit' }}>
          <Tooltip title="Edit">
            <IconButton onClick={this.handleEditClick}>
              <EditIcon sx={{ color: edit ? "#8f241d" : "#e3e3e5" }} />
            </IconButton>
          </Tooltip>
          {showSave &&
            <Tooltip title="Save">
              <IconButton onClick={this.pushData}>
                <SaveIcon sx={{ color: "#4287f5" }} />
              </IconButton>
            </Tooltip>
          }
        </StyledTableCell>
      </StyledTableRow>
    );
  }
}

export default class ConfigFeedback extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      client: new Client("none"),
      rows: [],
      addRow: false,
      queryData: {},

      // ✅ modules list from backend
      modules: [],
    };
    this.tableRef = React.createRef();
  }

  buildLanguagesMap(){
    const languages = {};
    if (this.props.languages) {
      const langs = this.props.languages;
      Object.keys(langs).forEach(key => {
        const f = langs[key];
        languages[f.iref] = { iref: f.iref, name: f.languageName, image: f.image, languageName: f.languageName };
      });
    }
    return languages;
  }

  getModules = () => {
    const q = { ...(this.state.queryData || {}) };

    // don't pass list/paging
    delete q.limit;
    delete q.orderBy;

    // ✅ IMPORTANT: don't filter modules list by module itself
    delete q.module;
    delete q.moduleLike;

    const data = this.state.client.getData(Config.API_URL_CONFIG_FEEDBACK_MODULES, q);

    if (data?.status === "OK" && Array.isArray(data.modules)) {
      this.setState({ modules: data.modules });
    } else if (Array.isArray(data?.modules)) {
      this.setState({ modules: data.modules });
    } else {
      this.setState({ modules: [] });
    }
  }


  getData = () => {
    const q = { orderBy: 'code', limit: DEFAULT_LIMIT, ...(this.state.queryData || {}) };
    const data = this.state.client.getData(Config.API_URL_CONFIG_FEEDBACK, q);
    this.setState({ rows: data?.FeedbackConfigs || [] });
  }

  componentDidMount(){
    this.getData();
    this.getModules();
  }

  applyFilters = (qFromFilters = {}) => {
    this.setState({ queryData: qFromFilters }, () => {
      this.getData();
      this.getModules();
    });
  }

  exportFiltered = () => {
    const q = { orderBy: "code", limit: EXPORT_LIMIT, ...(this.state.queryData || {}) };
    const data = this.state.client.getData(Config.API_URL_CONFIG_FEEDBACK, q);
    downloadCSV(`feedback_filtered_${new Date().toISOString().slice(0,19)}.csv`, toCSV(data?.FeedbackConfigs || []));
  }

  exportAll = () => {
    const q = { orderBy: "code", limit: EXPORT_LIMIT };
    const data = this.state.client.getData(Config.API_URL_CONFIG_FEEDBACK, q);
    downloadCSV(`feedback_all_${new Date().toISOString().slice(0,19)}.csv`, toCSV(data?.FeedbackConfigs || []));
  }

  rowAdded = () => {
    this.getData();
    this.getModules();
    this.setState({ addRow:false });
    this.state.client.setConfigLocalStorage();
    window.dispatchEvent(new Event("storageUpdate"));
  }

  toggleAdd = () => {
    const was = this.state.addRow;

    this.setState({ addRow: !was }, () => {
      // when turning ON addRow -> scroll table to top
      if (!was && this.tableRef.current) {
        const el = this.tableRef.current;
        if (el.scrollTo) el.scrollTo({ top: 0, behavior: 'smooth' });
        else el.scrollTop = 0;
      }
    });
  };

  render(){
    const languages = this.buildLanguagesMap();
    const rows = this.state.rows || [];
    const modules = this.state.modules || [];
    const defaultNewModule = modules.length ? modules[0] : "";

    return (
      <div>
        <FeedbackFilters
          languages={languages}
          modules={modules}
          applyFilters={this.applyFilters}
          onExportFiltered={this.exportFiltered}
          onExportAll={this.exportAll}
          resultsCount={rows.length}
        />

        <TableContainer component={Paper} sx={{ maxHeight: '78vh', overflow: 'auto' }} ref={this.tableRef}>
          <Table stickyHeader sx={{ minWidth: 900 }} aria-label="feedback table">
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton
                    aria-label="Add feedback"
                    title={this.state.addRow ? "Cancel add" : "Add feedback"}
                    onClick={this.toggleAdd}
                    size="small"
                  >
                    <Avatar sx={{ width: 24, height: 24, color: "#273442" }}>
                      {this.state.addRow ? <FaMinusCircle /> : <FaPlusCircle />}
                    </Avatar>
                  </IconButton>
                  iRef
                </StyledTableCell>
                <StyledTableCell align="left">Type</StyledTableCell>
                <StyledTableCell align="left">Module</StyledTableCell>
                <StyledTableCell align="left">Code</StyledTableCell>
                <StyledTableCell align="left">Message</StyledTableCell>
                <StyledTableCell align="left">Language</StyledTableCell>
                <StyledTableCell align="right" sx={{ position:'sticky', right:0, background:"#273442" }}>Action</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {this.state.addRow ? (
                <FeedbackRow
                  key="new"
                  edit={true}
                  rowAdded={this.rowAdded}
                  languages={languages}
                  modules={modules}
                  row={{ type:0, module: defaultNewModule, code:'', message:'', language:Object.values(languages)[0] }}
                />
              ) : null}

              {rows.map(r => (
                <FeedbackRow
                  key={r.iref}
                  row={r}
                  rowAdded={this.rowAdded}
                  languages={languages}
                  modules={modules}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }
}